import {
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useSnackbar } from 'notistack'
import { supabase } from '~/supabase-client'
import { supabaseGetter } from '~/api/supabase-getter'
import { useCurrentUser } from '~/common/use-current-user'

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90vw', sm: '60vw', md: '50vw', lg: '40vw' },
  bgcolor: 'background.paper',
  borderRadius: '0.5rem',
  boxShadow: 24,
  p: '1.5rem 2rem',
}

const generateTicketId = () => crypto.randomUUID()

export const InviteUserModal = ({ open, onClose, farmId }) => {
  const [roles, setRoles] = useState([])
  const [selectedRole, setSelectedRole] = useState('viewer')
  const [expiryDays, setExpiryDays] = useState(2)
  const [ticketId] = useState(() => generateTicketId())
  const [submitting, setSubmitting] = useState(false)
  const { currentUser } = useCurrentUser()
  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    async function fetchRoles() {
      const { data } = await supabaseGetter.organizationUserRoles.getAll()
      if (data) {
        setRoles(data)
        // Default to viewer if it exists
        const hasViewer = data.some(r => r.code === 'viewer')
        if (hasViewer) setSelectedRole('viewer')
        else if (data.length > 0) setSelectedRole(data[0].code)
      }
    }
    if (open) fetchRoles()
  }, [open])

  const inviteLink = `${window.location.origin}/login?inviteTicket=${ticketId}`
  const selectedRoleData = roles.find(r => r.code === selectedRole)

  const handleCopy = async () => {
    if (!farmId || !currentUser?.id) {
      enqueueSnackbar('Farm or user not available', { variant: 'error' })
      return
    }

    setSubmitting(true)
    try {
      const expiresAt = new Date(Date.now() + expiryDays * 24 * 60 * 60 * 1000).toISOString()
      const { error } = await supabase.from('invite_tickets').insert({
        id: ticketId,
        organization_id: farmId,
        role: selectedRole,
        invited_by: currentUser.id,
        expires_at: expiresAt,
      })

      if (error) {
        enqueueSnackbar(error.message, { variant: 'error' })
        return
      }

      await navigator.clipboard.writeText(inviteLink)
      enqueueSnackbar('Invite link copied to clipboard', { variant: 'success' })
      onClose && onClose()
    } catch (err) {
      enqueueSnackbar(err.message ?? 'Something went wrong', { variant: 'error' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <Typography variant="h5" gutterBottom>
          Invite a Member
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Share this link with someone to give them access to your farm. The link expires after
          the selected time and can only be used once to create an account.
        </Typography>

        <Divider sx={{ mb: 2 }} />

        {/* Role row — 2fr description : 1fr select */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr',
            alignItems: 'center',
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
            {selectedRoleData ? (
              <>
                <Typography variant="subtitle2">{selectedRoleData.label}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedRoleData.description}
                </Typography>
              </>
            ) : (
              <CircularProgress size={20} />
            )}
          </Box>
          <FormControl size="small" fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              label="Role"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              disabled={roles.length === 0}
            >
              {roles.map((r) => (
                <MenuItem key={r.code} value={r.code}>
                  {r.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Expiry row */}
        <Box sx={{ mb: 2 }}>
          <TextField
            label="Days until expiry"
            type="number"
            size="small"
            value={expiryDays}
            onChange={(e) => setExpiryDays(Math.max(1, parseInt(e.target.value, 10) || 1))}
            inputProps={{ min: 1 }}
            sx={{ width: 160 }}
          />
        </Box>

        {/* Link field */}
        <TextField
          label="Invite link"
          value={inviteLink}
          size="small"
          fullWidth
          slotProps={{ input: { readOnly: true } }}
          sx={{ mb: 3, '& input': { fontSize: '0.8rem', fontFamily: 'monospace' } }}
        />

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={onClose} disabled={submitting}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleCopy}
            disabled={submitting || !farmId || roles.length === 0}
          >
            {submitting ? 'Saving...' : 'Copy to clipboard'}
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}
